import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  CreditCard as CreditCardIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { CompanyService } from '../../services/CompanyService'

interface BillingStatus {
  current_balance: number
  stripe_balance_available?: boolean
  total_earned: number
  total_used: number
  monthly_cost: number
  next_billing_date: string
  pending_adjustments: number
  grace_period_active: boolean
  grace_period_ends?: string
  grace_period_hours_remaining?: number
}

interface BillingStatusIndicatorProps {
  companyId: string
  currentUsers: number
  onRefresh?: () => void
}

export const BillingStatusIndicator: React.FC<BillingStatusIndicatorProps> = ({
  companyId,
  currentUsers,
  onRefresh,
}) => {
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null)
  // Starts true: the first load begins on mount, so "unavailable" never flashes before it.
  const [loading, setLoading] = useState(true)
  // The last load was refused or failed. Kept apart from `loading` so a refused call never reads
  // as "Loading..." forever (SC-439). The caller mounts this only for people who may view the plan.
  const [failed, setFailed] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    loadBillingStatus()
  }, [companyId, currentUsers])

  const loadBillingStatus = async () => {
    try {
      setLoading(true)
      setFailed(false)
      const result = await CompanyService.getBillingStatus(companyId)
      if (result.success && result.billing_status) {
        setBillingStatus(result.billing_status)
      } else {
        setFailed(true)
        console.error('Failed to load billing status:', result.error)
      }
    } catch (error) {
      setFailed(true)
      console.error('Error loading billing status:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    await loadBillingStatus()
    if (onRefresh) {
      onRefresh()
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatTimeRemaining = (hours: number) => {
    if (hours < 1) {
      return 'Less than 1 hour'
    } else if (hours < 24) {
      return `${Math.floor(hours)} hours`
    } else {
      const days = Math.floor(hours / 24)
      const remainingHours = hours % 24
      return `${days} day${days > 1 ? 's' : ''}${remainingHours > 0 ? ` ${Math.floor(remainingHours)} hours` : ''}`
    }
  }

  // A failed refetch shows the failure state even when an earlier load left a status behind:
  // those chips would otherwise stay up as if current (the user count changed, the read failed).
  // While a retry runs the old status shows again until it answers.
  if (!billingStatus || (failed && !loading)) {
    const unavailable = failed && !loading
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip
          title={unavailable ? 'Billing status could not be loaded. Use refresh to try again.' : ''}
        >
          <Chip
            label={unavailable ? 'Billing unavailable' : 'Loading...'}
            size="small"
            data-testid="billing-status-placeholder"
            sx={{
              bgcolor: 'action.selected',
              color: 'text.secondary',
              fontSize: '0.75rem',
            }}
          />
        </Tooltip>
        {unavailable && (
          <Tooltip title="Refresh billing status">
            <IconButton size="small" onClick={handleRefresh} sx={{ color: 'text.secondary' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    )
  }

  const open = Boolean(anchorEl)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* Monthly Cost */}
      <Chip
        label={`$${billingStatus.monthly_cost.toFixed(2)}/month`}
        size="small"
        sx={(t) => ({
          bgcolor: alpha(t.palette.primary.main, 0.2),
          color: 'primary.main',
          fontSize: '0.75rem',
        })}
      />

      {/* Credit Balance - Always show, even if $0 */}
      <Chip
        label={`$${billingStatus.current_balance.toFixed(2)} credit${billingStatus.stripe_balance_available ? '' : ''}`}
        size="small"
        sx={(t) => ({
          bgcolor:
            billingStatus.current_balance > 0
              ? alpha(t.palette.success.main, 0.2)
              : t.palette.action.selected,
          color: billingStatus.current_balance > 0 ? 'success.main' : 'text.secondary',
          fontSize: '0.75rem',
        })}
      />

      {/* Pending Adjustments */}
      {billingStatus.pending_adjustments > 0 && (
        <Chip
          label={`${billingStatus.pending_adjustments} pending`}
          size="small"
          color="warning"
          sx={{ fontSize: '0.75rem' }}
        />
      )}

      {/* Grace Period Active */}
      {billingStatus.grace_period_active && (
        <Chip
          label={`Grace: ${formatTimeRemaining(billingStatus.grace_period_hours_remaining || 0)}`}
          size="small"
          color="info"
          sx={{ fontSize: '0.75rem' }}
        />
      )}

      {/* Status Details Button */}
      <Tooltip title="View billing details">
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Refresh Button */}
      <Tooltip title="Refresh billing status">
        {loading ? (
          <span style={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={loading}
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        ) : (
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={loading}
            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
      </Tooltip>

      {/* Billing Details Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 34, 50, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 300,
            maxWidth: 400,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            Billing Status
          </Typography>

          <List dense>
            {/* Current Users */}
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
              </ListItemIcon>
              <ListItemText
                primary="Current Users"
                secondary={currentUsers}
                primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
              />
            </ListItem>

            {/* Monthly Cost */}
            <ListItem>
              <ListItemIcon>
                <CreditCardIcon sx={{ color: 'info.main', fontSize: '1.2rem' }} />
              </ListItemIcon>
              <ListItemText
                primary="Monthly Cost"
                secondary={`$${billingStatus.monthly_cost.toFixed(2)}`}
                primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
              />
            </ListItem>

            {/* Next Billing Date */}
            {billingStatus.next_billing_date && (
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Next Billing Date"
                  secondary={formatDate(billingStatus.next_billing_date)}
                  primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            )}

            {/* Credit Balance - Always show */}
            <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            <ListItem>
              <ListItemIcon>
                <CreditCardIcon sx={{ color: billingStatus.current_balance > 0 ? 'success.main' : 'text.secondary', fontSize: '1.2rem' }} />
              </ListItemIcon>
              <ListItemText
                primary="Credit Balance"
                secondary={`$${billingStatus.current_balance.toFixed(2)}${billingStatus.stripe_balance_available ? ' (from Stripe)' : ' (from Parse)'}`}
                primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: billingStatus.current_balance > 0 ? 'success.main' : 'text.secondary' }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Total Credits Earned"
                secondary={`$${billingStatus.total_earned.toFixed(2)}`}
                primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primary="Total Credits Used"
                secondary={`$${billingStatus.total_used.toFixed(2)}`}
                primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
              />
            </ListItem>

            {/* Pending Adjustments */}
            {billingStatus.pending_adjustments > 0 && (
              <>
                <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Pending Adjustments"
                    secondary={`${billingStatus.pending_adjustments} items`}
                    primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ color: 'warning.main' }}
                  />
                </ListItem>
              </>
            )}

            {/* Grace Period */}
            {billingStatus.grace_period_active && (
              <>
                <Divider sx={{ my: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon sx={{ color: 'info.main', fontSize: '1.2rem' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Grace Period Active"
                    secondary={`${formatTimeRemaining(billingStatus.grace_period_hours_remaining || 0)} remaining`}
                    primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ color: 'info.main' }}
                  />
                </ListItem>
                {billingStatus.grace_period_ends && (
                  <ListItem>
                    <ListItemText
                      primary="Grace Period Ends"
                      secondary={formatDate(billingStatus.grace_period_ends)}
                      primaryTypographyProps={{ color: 'white', fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    />
                  </ListItem>
                )}
              </>
            )}
          </List>
        </Box>
      </Popover>
    </Box>
  )
}
